<template>
  <modal
    name="activate-temporary-agreement"
    class="modal-inner"
    transition="pop-out"
    :width="modalWidth"
    :focus-trap="true"
    height="auto"
    :scrollable="true"
    :adaptive="true"
    :reset="true"
    @before-open="beforeOpen"
    @closed="resetState"
  >
    <span class="close-button" @click="hide()">
      <BaseIcon icon="times-circle" class="text-white" />
    </span>
    <div class="modal-header text-lg bg-primary-50 font-serif">
      {{ $t("agreements.activate_draft.title") }}
    </div>
    <div class="modal-body">
      <div v-if="agreement">
        <!-- Agreement Info Banner -->
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div class="flex items-start gap-2">
            <BaseIcon icon="clipboard-list" class="text-blue-500 mt-0.5" />
            <div>
              <div class="text-sm font-semibold text-blue-800">{{ agreement.agreement_number }}</div>
              <div class="text-sm text-blue-700">{{ agreement.customer_name }}</div>
              <div class="text-xs text-blue-600 mt-1">{{ $t("agreements.activate_draft.info") }}</div>
            </div>
          </div>
        </div>

        <!-- Product Selection -->
        <div v-if="orderSelectionItems.length > 0">
          <div class="w-full text-sm font-bold italic px-2 mb-3">
            {{ $t("agreements.activate_draft.select_products") }}
          </div>
          <div class="flex items-center gap-2 mb-3 pb-3 border-b border-gray-200">
            <input
              id="activate-select-all"
              type="checkbox"
              :checked="allProductsSelected"
              @change="toggleAllProducts"
              class="w-4 h-4 text-blue-600 rounded"
            />
            <label for="activate-select-all" class="text-sm font-medium text-gray-700">
              {{ $t('agreements.select_all') }} ({{ orderSelectionItems.length }})
            </label>
          </div>

          <div class="max-h-96 overflow-y-auto space-y-2 pr-1">
            <label
              v-for="item in orderSelectionItems"
              :key="item.key"
              :for="'activate-' + item.key"
              class="flex items-start gap-3 rounded-lg border border-gray-200 px-3 py-2 hover:bg-gray-50 cursor-pointer"
            >
              <input
                :id="'activate-' + item.key"
                v-model="selectedProductKeys"
                type="checkbox"
                :value="item.key"
                class="w-4 h-4 text-blue-600 rounded mt-0.5"
              />
              <div class="flex-1 min-w-0">
                <div class="text-sm font-medium text-gray-800">{{ item.label }}</div>
                <div v-if="item.subLabel" class="text-xs text-gray-500">{{ item.subLabel }}</div>
              </div>
              <div class="text-right text-xs text-gray-500 whitespace-nowrap">
                <div>{{ $t('agreement_create.quantity') }}: {{ item.qty }}</div>
                <div v-if="item.price > 0">{{ formatAmount(item.price) }}</div>
              </div>
            </label>
          </div>
        </div>

        <div v-else class="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {{ $t('agreements.activate_draft.no_products') }}
        </div>

        <div class="mx-2 border-b border-secondary-400 my-5"></div>

        <!-- Order-specific fields (required when generating order) -->
        <ValidationObserver ref="formActivate">
          <div class="w-full text-sm font-bold italic px-2 mb-3">
            {{ $t('agreements.activate_draft.order_details') }}
          </div>

          <!-- Simkort & Larmcentral -->
          <div class="flex flex-wrap">
            <div class="w-full md:w-1/3 px-2 py-2">
              <BaseSelect v-model="sim_card" :options="sim_operators" :field_name="$t('admin_order_alarm.sim_card')" rules="required" />
            </div>
            <div class="w-full md:w-1/3 px-2 py-2">
              <BaseSelect v-model="alarm_center" :options="[{ name: $t('admin_order_alarm.none'), _id: '-' }, ...agreementAlarmCenters]" nameSelector="name" optionSelector="_id" :field_name="$t('admin_order_alarm.alarm_center')" rules="required" />
            </div>
            <div class="w-full md:w-1/3 px-2 py-2">
              <BaseSwitch v-model="direct_dispatch" :field_name="$t('alarm_pdf.direct_dispatch')" />
            </div>
            <div class="w-full md:w-1/3 px-2 py-2">
              <BaseSwitch v-model="insurance" :field_name="$t('admin_order_alarm.insurance')" />
            </div>
          </div>

          <!-- Riskprofil & Avsändaradress -->
          <div class="flex flex-wrap">
            <div class="w-full md:w-1/3 px-2 py-2">
              <BaseSelect v-model="risk_profile" :options="riskProfileOptions" nameSelector="label" optionSelector="value" rules="required" :field_name="$t('admin_order_alarm.risk_profile')" />
            </div>
            <div class="w-full md:w-1/3 px-2 py-2" v-if="!isSafe4Selected">
              <BaseInput v-model="from_address" type="text" rules="required" :field_name="$t('admin_order_alarm.from_address')" />
            </div>
          </div>
          <!-- Safe-4 (SEC-370): the address split into street / postal code / city,
               as on the order and device forms, so the devices carry a postcode
               Safe-4 accepts. -->
          <div class="flex flex-wrap" v-if="isSafe4Selected">
            <div class="w-full md:w-1/3 px-2 py-2">
              <BaseInput v-model="from_address_parts.street" type="text" rules="required|max:30" maxlength="30" :field_name="$t('safe4_form.street')" />
            </div>
            <div class="w-full md:w-1/3 px-2 py-2">
              <BaseInput v-model="from_address_parts.zip" type="text" :rules="`required|safe4_postcode:${agreementCountry()}`" :field_name="$t('safe4_form.postal_code')" />
            </div>
            <div class="w-full md:w-1/3 px-2 py-2">
              <BaseInput v-model="from_address_parts.city" type="text" rules="required" :field_name="$t('safe4_form.city')" />
            </div>
          </div>

          <!-- Produktnamn -->
          <div class="flex flex-wrap">
            <div class="w-full px-2 py-2">
              <BaseTextarea rows="2" v-model="alarms_name" :field_name="$t('admin_order_alarm.product_names')" rules="required" />
            </div>
          </div>

          <!-- Kommentar & Kontaktpersoner -->
          <div class="flex flex-wrap">
            <div class="w-full md:w-1/2 px-2 py-2">
              <BaseTextarea rows="3" v-model="comment" :field_name="$t('admin_order_alarm.comment_order_handling')" />
            </div>
            <div class="w-full md:w-1/2 px-2 py-2">
              <BaseTextarea rows="3" v-model="contact_person" :field_name="$t('admin_order_alarm.contact_persons')" rules="required" />
            </div>
          </div>

          <!-- Lösenord -->
          <div class="flex flex-wrap">
            <div class="w-full md:w-1/3 px-2 py-2">
              <BaseInput v-model="alarm_password" vid="alarm-password" type="text" :field_name="$t('admin_order_alarm.password')" :rules="isSafe4Selected ? 'required|max:20|safe4_password' : 'required'" />
            </div>
          </div>

          <!-- SMS Numbers -->
          <div class="flex flex-wrap">
            <div class="w-full md:w-1/3 px-2 py-2">
              <div class="w-full text-gray-600 text-sm font-bold pb-2 font-serif">{{ $t('admin_order_alarm.sms') }}</div>
              <div class="w-full flex flex-wrap mt-2" v-for="(n, i) in sms_numbers" :key="i">
                <div class="w-11/12">
                  <ValidationProvider :name="$t('admin_order_alarm.sms_number') + ' ' + (i + 1)" rules="required|sms_msisdn|max:15" v-slot="{ classes, errors }">
                    <div class="input-validate" :class="classes">
                      <input v-model="n.msisdn" type="text" maxlength="15" class="w-full bg-gray-100 rounded text-sm text-gray-900 focus:outline-none border-b-2 border-gray-300 transition duration-500 px-3 py-2" />
                      <span>{{ errors[0] }}</span>
                    </div>
                  </ValidationProvider>
                </div>
                <button class="w-1/12 text-red-500 pl-2" @click.prevent="removeSMSInput(i)"><BaseIcon icon="minus-circle" /></button>
              </div>
              <div class="w-full">
                <button class="btn-blue-outline mt-2" @click.prevent="addSMSInput()">{{ $t('admin_order_alarm.add') }} <BaseIcon icon="plus" class="ml-1" /></button>
              </div>
            </div>
          </div>

          <!-- Leveransperiod -->
          <div class="flex flex-wrap">
            <div class="w-full md:w-1/3 px-2 py-2">
              <ValidationProvider :name="$t('admin_order_alarm.delivery_deadline')" rules="required" v-slot="{ classes, errors }">
                <div class="input-validate" :class="classes">
                  <label class="text-gray-600 text-sm font-bold pb-2 font-serif">{{ $t('admin_order_alarm.delivery_deadline') }}<b class="text-red-500 ml-1">*</b></label>
                  <date-picker
                    :lang="datePickerLocale"
                    :key="date_picker_langKey"
                    class="mt-2"
                    v-model="delivery_period"
                    value-type="format"
                    format="YYYY-MM-DD"
                    :editable="false"
                    :clearable="false"
                  />
                  <span>{{ errors[0] }}</span>
                </div>
              </ValidationProvider>
            </div>
          </div>

          <div class="mx-2 border-b border-secondary-400 my-5"></div>

          <!-- Generella frågor (optional) -->
          <div class="w-full text-sm font-bold italic px-2 mb-3">{{ $t('admin_order_alarm.general_questions') }}</div>
          <div class="flex flex-wrap">
            <div class="w-full md:w-1/2 px-2 py-2">
              <BaseInput v-model="outdoor_work" type="text" :field_name="$t('admin_order_alarm.outdoor_work')" />
            </div>
            <div class="w-full md:w-1/2 px-2 py-2">
              <BaseInput v-model="alert_members" type="text" :field_name="$t('admin_order_alarm.alert_colleagues')" />
            </div>
            <div class="w-full md:w-1/2 px-2 py-2">
              <BaseInput v-model="indoor_tracking" type="text" :field_name="$t('admin_order_alarm.indoor_location')" />
            </div>
            <div class="w-full md:w-1/2 px-2 py-2">
              <BaseTextarea rows="3" v-model="email_sms_detail" :field_name="$t('admin_order_alarm.sms_email_question')" />
            </div>
          </div>

          <div class="mx-2 border-b border-secondary-400 my-5"></div>

          <!-- Larmunderlag (optional) -->
          <div class="w-full text-sm font-bold italic px-2 mb-3">{{ $t('admin_order_alarm.alarm_basis') }}</div>
          <div class="flex flex-wrap">
            <div class="w-full md:w-1/2 px-2 py-2">
              <BaseInput v-model="total_user" type="text" :field_name="$t('admin_order_alarm.total_users')" />
            </div>
            <div class="w-full md:w-1/2 px-2 py-2">
              <BaseInput v-model="work_task" type="text" :field_name="$t('admin_order_alarm.work_tasks')" />
            </div>
            <div class="w-full md:w-1/2 px-2 py-2">
              <BaseInput v-model="working_place" type="text" :field_name="$t('admin_order_alarm.workplace_location')" />
            </div>
            <div class="w-full md:w-1/2 px-2 py-2">
              <BaseInput v-model="risk_detail" type="text" :field_name="$t('admin_order_alarm.risk_assessment')" />
            </div>
            <div class="w-full md:w-1/2 px-2 py-2">
              <BaseTextarea rows="3" v-model="other_info" :field_name="$t('admin_order_alarm.other_info')" />
            </div>
          </div>
        </ValidationObserver>
      </div>
    </div>
    <div class="modal-footer">
      <div class="footer-container clearfix">
        <button class="btn-red-outline px-3 py-2 float-left" @click="hide()">
          {{ $t("user_actions.cancel") }}
          <BaseIcon icon="times-circle" class="ml-1" />
        </button>
        <button class="btn-secondary-outline px-3 py-2 float-left ml-2" @click="activateWithoutOrder()">
          {{ $t('agreements.activate_draft.skip_order') }}
        </button>
        <button
          class="btn-green px-3 py-2 float-right disabled:opacity-60 disabled:cursor-not-allowed"
          :disabled="!canGenerateOrder"
          @click="activateWithOrder()"
        >
          {{ $t('agreements.activate_draft.generate_order') }}
          <BaseIcon icon="shopping-cart" class="ml-1" />
        </button>
      </div>
    </div>
  </modal>
</template>

<script>
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/locale/sv";
import "vue2-datepicker/locale/nb";
import "@/utils/datePickerDutch";
import "vue2-datepicker/index.css";
import { RISK_PROFILE, normalizeRiskProfile } from "@/utils/alarmEnums";
import siteContext from "@/utils/siteContext";
import { splitAddressLine } from "@/utils/safe4Postcodes";

export default {
  name: "ActivateTemporaryAgreement",
  components: { DatePicker },
  props: {
    sellers: {
      type: Array,
      default: () => [],
    },
    alarm_centers: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      agreement: null,
      modalWidth: 900,
      selectedProductKeys: [],
      sim_card: "",
      alarm_center: "",
      direct_dispatch: false,
      insurance: true,
      risk_profile: "",
      from_address: "",
      // Safe-4 (SEC-370): the address split up, only used for Safe-4
      from_address_parts: { street: "", zip: "", city: "" },
      // Safe-4: the AlarmCenter _id that is Safe-4 (empty = integration off)
      safe4_center_id: "",
      alarms_name: "",
      comment: "",
      contact_person: "",
      alarm_password: "",
      sms_numbers: [],
      delivery_period: "",
      outdoor_work: "",
      alert_members: "",
      indoor_tracking: "",
      email_sms_detail: "",
      total_user: "",
      work_task: "",
      working_place: "",
      risk_detail: "",
      other_info: "",
      date_picker_lang: this.$i18n.locale,
      date_picker_langKey: 0,
      productsFromDB: [],
      // alarm centers of an agreement from another country than the portal's -
      // the alarm_centers prop holds the portal's list
      countryAlarmCenters: null,
    };
  },
  computed: {
    /** True when the chosen alarm central is Safe-4 (SEC-370). */
    isSafe4Selected() {
      return !!this.safe4_center_id && this.alarm_center == this.safe4_center_id;
    },
    riskProfileOptions() {
      return [
        { value: RISK_PROFILE.THREAT, label: this.$t('admin_order_alarm.threat_alarm') },
        { value: RISK_PROFILE.EMERGENCY, label: this.$t('admin_order_alarm.emergency_alarm') },
      ];
    },
    agreementAlarmCenters() {
      return this.countryAlarmCenters || this.alarm_centers;
    },
    sim_operators() {
      return this.productsFromDB
        .filter(p => p.category === 'simcard_supplier')
        .map(p => p.name);
    },
    datePickerLocale() {
      return this.date_picker_lang === "no" ? "nb" : this.date_picker_lang;
    },
    orderSelectionItems() {
      if (!this.agreement || !this.agreement.products) return [];
      const items = [];
      this.agreement.products.forEach(product => {
        if ((product.qty || 0) > 0) {
          items.push({
            key: `product-${product.name}-${product.type}`,
            label: product.name,
            subLabel: product.type,
            qty: product.qty,
            price: product.price || product.cash_price || 0,
            // SEC-405: without its variants - they are their own items below
            product: { ...product, variants: [] },
          });
        }

        // SEC-405: a draft edited in the agreement form keeps the chosen
        // variants inside the product (qty 0 on the product itself). Each
        // ordered variant is offered as its own order line.
        (product.variants || []).forEach(variant => {
          if (!((variant.qty || 0) > 0)) return;
          const name = `${product.name} - ${variant.name}`;
          // the agreement form edits rental_price, so it wins when set
          const price = variant.rental_price || variant.price || null;
          items.push({
            key: `variant-${variant._id || name}-${product.type}`,
            label: name,
            subLabel: product.type,
            qty: variant.qty,
            price: price || variant.cash_price || 0,
            product: {
              display_name: `${product.display_name || product.name} - ${variant.name}`,
              name: name,
              type: product.type,
              category: product.category || 'other',
              is_software: product.is_software || false,
              variant_id: variant._id,
              variant_name: variant.name,
              articleNumber: variant.articleNumber || '',
              qty: variant.qty,
              price: price,
              cash_price: variant.cash_price || null,
            },
          });
        });
      });
      return items;
    },
    allProductsSelected() {
      return this.orderSelectionItems.length > 0 &&
        this.selectedProductKeys.length === this.orderSelectionItems.length;
    },
    canGenerateOrder() {
      return this.selectedProductKeys.length > 0;
    },
  },
  methods: {
    beforeOpen(event) {
      if (event.params && event.params.agreement) {
        this.agreement = event.params.agreement;
        this.selectedProductKeys = this.orderSelectionItems.map(item => item.key);
        // Pre-fill from agreement if values exist
        this.sim_card = this.agreement.sim_card || "";
        this.alarm_center = this.agreement.alarm_center || "";
        this.direct_dispatch = this.agreement.direct_dispatch || false;
        this.insurance = this.agreement.insurance !== undefined ? this.agreement.insurance : true;
        this.risk_profile = normalizeRiskProfile(this.agreement.risk_profile || "");
        this.from_address = this.agreement.from_address || "";
        // Safe-4: the stored one-line address, split, so nothing is retyped
        this.from_address_parts = splitAddressLine(this.from_address);
        this.getSafe4CenterId();
        this.alarms_name = this.agreement.alarms_name || "";
        this.comment = this.agreement.comment || "";
        this.contact_person = this.agreement.contact_person || "";
        this.alarm_password = this.agreement.alarm_password || "";
        this.sms_numbers = this.agreement.sms_numbers && this.agreement.sms_numbers.length > 0
          ? this.agreement.sms_numbers.map(n => ({ msisdn: n.msisdn || n }))
          : [];
        this.delivery_period = this.agreement.delivery_period || "";
        this.outdoor_work = this.agreement.outdoor_work || "";
        this.alert_members = this.agreement.alert_members || "";
        this.indoor_tracking = this.agreement.indoor_tracking || "";
        this.email_sms_detail = this.agreement.email_sms_detail || "";
        this.total_user = this.agreement.total_user || "";
        this.work_task = this.agreement.work_task || "";
        this.working_place = this.agreement.working_place || "";
        this.risk_detail = this.agreement.risk_detail || "";
        this.other_info = this.agreement.other_info || "";
      }
      this.fetchProducts();
      this.fetchCountryAlarmCenters();
    },
    // Safe-4: the one-line address built from the split one
    safe4FromAddressLine() {
      const parts = this.from_address_parts || {};
      return [parts.street, parts.zip, parts.city].map((v) => String(v || "").trim()).filter(Boolean).join(" ");
    },
    // Which alarm central is Safe-4. Fails quietly -> no Safe-4 behavior.
    getSafe4CenterId() {
      this.axios
        .get(`${process.env.VUE_APP_SERVER_URL}/safe4/config`)
        .then((res) => { this.safe4_center_id = (res.data && res.data.safe4_alarm_center_id) || ""; })
        .catch(() => { this.safe4_center_id = ""; });
    },
    // The catalog and alarm centers follow the agreement's country, not the
    // portal's (e.g. a Norwegian draft activated from the Swedish portal).
    agreementCountry() {
      return (this.agreement && this.agreement.country) || siteContext.getSiteContext();
    },
    async fetchCountryAlarmCenters() {
      this.countryAlarmCenters = null;
      // not compared to getSiteContext() - on the Swedish domain it can say
      // 'no' (Norwegian UI language) while the server still answers for 'se'
      if (!(this.agreement && this.agreement.country)) return;
      const country = this.agreement.country;
      try {
        const response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/alarm-central`, { params: { country } });
        if (country === this.agreementCountry()) this.countryAlarmCenters = response.data.data;
      } catch (error) {
        this.handleError(error);
      }
    },
    resetState() {
      this.agreement = null;
      this.selectedProductKeys = [];
      this.sim_card = "";
      this.alarm_center = "";
      this.direct_dispatch = false;
      this.insurance = true;
      this.risk_profile = "";
      this.from_address = "";
      this.alarms_name = "";
      this.comment = "";
      this.contact_person = "";
      this.alarm_password = "";
      this.from_address_parts = { street: "", zip: "", city: "" };
      this.sms_numbers = [];
      this.delivery_period = "";
      this.outdoor_work = "";
      this.alert_members = "";
      this.indoor_tracking = "";
      this.email_sms_detail = "";
      this.total_user = "";
      this.work_task = "";
      this.working_place = "";
      this.risk_detail = "";
      this.other_info = "";
      this.productsFromDB = [];
    },
    hide() {
      this.$modal.hide("activate-temporary-agreement");
    },
    toggleAllProducts() {
      if (this.allProductsSelected) {
        this.selectedProductKeys = [];
      } else {
        this.selectedProductKeys = this.orderSelectionItems.map(item => item.key);
      }
    },
    buildSelectedProducts() {
      return this.orderSelectionItems
        .filter(item => this.selectedProductKeys.includes(item.key))
        .map(item => item.product);
    },
    addSMSInput() {
      this.sms_numbers.push({ msisdn: '' });
    },
    removeSMSInput(index) {
      this.sms_numbers.splice(index, 1);
    },
    async fetchProducts() {
      try {
        const response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/products`, { params: { country: this.agreementCountry() } });
        this.productsFromDB = response.data.data;
      } catch (error) {
        console.error('Failed to fetch products:', error);
      }
    },
    async activateWithOrder() {
      if (!this.canGenerateOrder) return;
      const isValid = await this.$refs.formActivate.validate();
      if (!isValid) return;
      await this.doActivate(true);
    },
    async activateWithoutOrder() {
      await this.doActivate(false);
    },
    async doActivate(generateOrder) {
      try {
        const payload = {
          generate_order: generateOrder,
          order_products: generateOrder ? this.buildSelectedProducts() : [],
          sim_card: this.sim_card,
          alarm_center: this.alarm_center,
          direct_dispatch: this.direct_dispatch,
          insurance: this.insurance,
          risk_profile: this.risk_profile,
          // Safe-4: the one-line address is built from the split one
          from_address: this.isSafe4Selected ? this.safe4FromAddressLine() : this.from_address,
          from_address_parts: this.isSafe4Selected ? this.from_address_parts : { street: "", zip: "", city: "" },
          alarms_name: this.alarms_name,
          comment: this.comment,
          contact_person: this.contact_person,
          alarm_password: this.alarm_password,
          sms_numbers: this.sms_numbers,
          delivery_period: this.delivery_period,
          outdoor_work: this.outdoor_work,
          alert_members: this.alert_members,
          indoor_tracking: this.indoor_tracking,
          email_sms_detail: this.email_sms_detail,
          total_user: this.total_user,
          work_task: this.work_task,
          working_place: this.working_place,
          risk_detail: this.risk_detail,
          other_info: this.other_info,
        };

        await this.axios.put(
          `${process.env.VUE_APP_SERVER_URL}/agreement/activate-draft/${this.agreement._id}`,
          payload
        );

        let message = this.$t('agreements.activate_draft.success');
        if (generateOrder) {
          message += ' - ' + this.$t('agreements.activate_draft.order_created');
        }
        this.handleSuccess(message);
        this.$emit("agreement-activated");
        this.hide();
      } catch (error) {
        // Safe-4 (SEC-370): the server refused values Safe-4 would not accept
        if (!this.handleSafe4Rejection(error, this.$refs.formActivate)) this.handleError(error);
      }
    },
  },
  watch: {
    "$i18n.locale"(newLocale) {
      this.date_picker_lang = newLocale;
      this.date_picker_langKey++;
    },
  },
};
</script>
